import { adminService } from "@/api/admin/admin.service";
import type {
  TCompanySummary,
  TCostTokenUsage,
  TEngagement,
  TKeyMetrics,
  TLeaderboardCompany,
  TUserSummary,
} from "@/api/admin/admin.types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import { Link } from "@tanstack/react-router";
import { EmptyState } from "@/components/shared/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  ArrowRight,
  Calendar,
  ChevronDown,
  ChevronsUpDown,
  Download,
  FileText,
  Loader2,
  RotateCw,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const LEADERBOARD_LIMIT = 20;

// Helper function to format time from minutes to "Xh Ymins"
const formatTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours === 0) {
    return `${mins}mins`;
  }
  return `${hours}h ${mins}mins`;
};

export function AnalyticsPage() {
  // Derive chart colors from CSS --primary so they match brand in both light/dark mode
  const [chartPrimary, chartPrimaryLight, chartPrimaryMuted] = (() => {
    if (typeof window === "undefined") return ["#0052FF", "#4d8aff", "#99bfff"];
    const raw = getComputedStyle(document.documentElement).getPropertyValue("--primary").trim();
    const [h = 221, s = 100, l = 50] = raw.split(/\s+/).map(parseFloat);
    return [
      `hsl(${h}, ${s}%, ${l}%)`,
      `hsl(${h}, ${s}%, ${Math.min(l + 18, 80)}%)`,
      `hsl(${h}, ${Math.round(s * 0.55)}%, ${Math.min(l + 28, 82)}%)`,
    ];
  })();

  const stackColors = {
    item1: chartPrimary,
    item2: chartPrimaryLight,
    item3: chartPrimaryMuted,
  };

  // State for filters
  const [costModel, setCostModel] = useState<string>("");
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [sortBy, setSortBy] = useState<"current_score" | "onboarding_score" | "name">("current_score");

  // State for duration filter
  const [duration, setDuration] = useState<"24h" | "7d" | "2w" | "1m" | "4m" | "custom">("1m");
  const [customDateFrom, setCustomDateFrom] = useState<Date | null>(null);
  const [customDateTo, setCustomDateTo] = useState<Date | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const [isReportDownloading, setIsReportDownloading] = useState(false);

  // Calculate date range based on duration
  const getDateRange = () => {
    if (duration === "custom" && customDateFrom && customDateTo) {
      return {
        dateFrom: customDateFrom,
        dateTo: customDateTo,
      };
    }

    const dateTo = new Date();
    const dateFrom = new Date();

    switch (duration) {
      case "24h":
        dateFrom.setHours(dateFrom.getHours() - 24);
        break;
      case "7d":
        dateFrom.setDate(dateFrom.getDate() - 7);
        break;
      case "2w":
        dateFrom.setDate(dateFrom.getDate() - 14);
        break;
      case "1m":
        dateFrom.setDate(dateFrom.getDate() - 30);
        break;
      case "4m":
        dateFrom.setDate(dateFrom.getDate() - 120);
        break;
      default:
        dateFrom.setDate(dateFrom.getDate() - 30);
    }

    return { dateFrom, dateTo };
  };

  const { dateFrom, dateTo } = getDateRange();

  // Format date range display
  const formatDateRangeDisplay = () => {
    if (duration === "custom" && customDateFrom && customDateTo) {
      return `${customDateFrom.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} - ${customDateTo.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
    }
    return `${dateFrom.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} - ${dateTo.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
  };

  // Get duration label
  const getDurationLabel = (value: typeof duration) => {
    const labels: Record<typeof value, string> = {
      "24h": "Last 24 hours",
      "7d": "Last 7 days",
      "2w": "Last 2 weeks",
      "1m": "Last Month",
      "4m": "Last 4 months",
      custom: "Custom",
    };
    return labels[value];
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // State for Key Metrics
  const [keyMetrics, setKeyMetrics] = useState<TKeyMetrics | null>(null);
  const [keyMetricsLoading, setKeyMetricsLoading] = useState(true);
  const [keyMetricsError, setKeyMetricsError] = useState<string | null>(null);

  // State for User Summary
  const [userSummary, setUserSummary] = useState<TUserSummary | null>(null);
  const [userSummaryLoading, setUserSummaryLoading] = useState(true);
  const [userSummaryError, setUserSummaryError] = useState<string | null>(null);
  const [userSummaryFilter, setUserSummaryFilter] = useState<"role" | "status">("role");

  // State for Engagement
  const [engagement, setEngagement] = useState<TEngagement | null>(null);
  const [engagementLoading, setEngagementLoading] = useState(true);
  const [engagementError, setEngagementError] = useState<string | null>(null);

  // State for Cost Token Usage
  const [costTokenUsage, setCostTokenUsage] = useState<TCostTokenUsage | null>(null);
  const [costTokenLoading, setCostTokenLoading] = useState(true);
  const [costTokenError, setCostTokenError] = useState<string | null>(null);

  // State for Company Summary
  const [companySummary, setCompanySummary] = useState<TCompanySummary | null>(null);
  const [companySummaryLoading, setCompanySummaryLoading] = useState(true);
  const [companySummaryError, setCompanySummaryError] = useState<string | null>(null);

  // State for Leaderboard
  const [leaderboard, setLeaderboard] = useState<TLeaderboardCompany[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [leaderboardError, setLeaderboardError] = useState<string | null>(null);

  const fetchKeyMetrics = useCallback(async () => {
    setKeyMetricsLoading(true);
    setKeyMetricsError(null);

    const params: {
      duration?: "24h" | "7d" | "2w" | "1m" | "4m" | "custom";
      date_from?: string;
      date_to?: string;
    } = { duration };

    if (duration === "custom" && customDateFrom && customDateTo) {
      params.date_from = customDateFrom.toISOString();
      params.date_to = customDateTo.toISOString();
    }

    const { data, error } = await adminService.getAnalyticsKeyMetrics(params);
    if (error) {
      setKeyMetricsError("Failed to load key metrics");
    } else if (data?.data) {
      setKeyMetrics(data.data);
    }
    setKeyMetricsLoading(false);
  }, [duration, customDateFrom, customDateTo]);

  useEffect(() => { void fetchKeyMetrics(); }, [fetchKeyMetrics]);

  const fetchUserSummary = useCallback(async () => {
    setUserSummaryLoading(true);
    setUserSummaryError(null);

    const params: {
      duration?: "24h" | "7d" | "2w" | "1m" | "4m" | "custom";
      date_from?: string;
      date_to?: string;
    } = { duration };

    if (duration === "custom" && customDateFrom && customDateTo) {
      params.date_from = customDateFrom.toISOString();
      params.date_to = customDateTo.toISOString();
    }

    const { data, error } = await adminService.getAnalyticsUserSummary(params);
    if (error) {
      setUserSummaryError("Failed to load user summary");
    } else if (data?.data) {
      setUserSummary(data.data);
    }
    setUserSummaryLoading(false);
  }, [duration, customDateFrom, customDateTo]);

  useEffect(() => { void fetchUserSummary(); }, [fetchUserSummary]);

  const fetchEngagement = useCallback(async () => {
    setEngagementLoading(true);
    setEngagementError(null);

    const params: {
      duration?: "24h" | "7d" | "2w" | "1m" | "4m" | "custom";
      date_from?: string;
      date_to?: string;
    } = { duration };

    if (duration === "custom" && customDateFrom && customDateTo) {
      params.date_from = customDateFrom.toISOString();
      params.date_to = customDateTo.toISOString();
    }

    const { data, error } = await adminService.getAnalyticsEngagement(params);
    if (error) {
      setEngagementError("Failed to load engagement data");
    } else if (data?.data) {
      setEngagement(data.data);
    }
    setEngagementLoading(false);
  }, [duration, customDateFrom, customDateTo]);

  useEffect(() => { void fetchEngagement(); }, [fetchEngagement]);

  const fetchCostTokenUsage = useCallback(async () => {
    setCostTokenLoading(true);
    setCostTokenError(null);

    const params: {
      model_name?: string;
      duration?: "24h" | "7d" | "2w" | "1m" | "4m" | "custom";
      date_from?: string;
      date_to?: string;
    } = { model_name: costModel || undefined, duration };

    if (duration === "custom" && customDateFrom && customDateTo) {
      params.date_from = customDateFrom.toISOString();
      params.date_to = customDateTo.toISOString();
    }

    const { data, error } = await adminService.getAnalyticsCostTokenUsage(params);
    if (error) {
      setCostTokenError("Failed to load cost and token usage");
    } else if (data?.data) {
      setCostTokenUsage(data.data);
    }
    setCostTokenLoading(false);
  }, [costModel, duration, customDateFrom, customDateTo]);

  useEffect(() => { void fetchCostTokenUsage(); }, [fetchCostTokenUsage]);

  const fetchCompanySummary = useCallback(async () => {
    setCompanySummaryLoading(true);
    setCompanySummaryError(null);

    const params: {
      duration?: "24h" | "7d" | "2w" | "1m" | "4m" | "custom";
      date_from?: string;
      date_to?: string;
    } = { duration };

    if (duration === "custom" && customDateFrom && customDateTo) {
      params.date_from = customDateFrom.toISOString();
      params.date_to = customDateTo.toISOString();
    }

    const { data, error } = await adminService.getAnalyticsCompanySummary(params);
    if (error) {
      setCompanySummaryError("Failed to load company summary");
    } else if (data?.data) {
      setCompanySummary(data.data);
    }
    setCompanySummaryLoading(false);
  }, [duration, customDateFrom, customDateTo]);

  useEffect(() => { void fetchCompanySummary(); }, [fetchCompanySummary]);

  const fetchLeaderboard = useCallback(async () => {
    setLeaderboardLoading(true);
    setLeaderboardError(null);

    const params: {
      sort_by: "current_score" | "onboarding_score" | "name";
      limit: number;
      duration?: "24h" | "7d" | "2w" | "1m" | "4m" | "custom";
      date_from?: string;
      date_to?: string;
    } = {
      sort_by: sortBy,
      limit: LEADERBOARD_LIMIT,
      duration,
    };

    if (duration === "custom" && customDateFrom && customDateTo) {
      params.date_from = customDateFrom.toISOString();
      params.date_to = customDateTo.toISOString();
    }

    const { data, error } = await adminService.getAnalyticsLeaderboard(params);
    if (error) {
      setLeaderboardError("Failed to load leaderboard");
    } else if (data?.data) {
      setLeaderboard(data.data.companies);
    }
    setLeaderboardLoading(false);
  }, [sortBy, duration, customDateFrom, customDateTo]);

  useEffect(() => { void fetchLeaderboard(); }, [fetchLeaderboard]);

  // Transform User Summary data for chart
  const userSummaryChartData = userSummary
    ? userSummaryFilter === "status"
      ? [
          {
            name: "Active",
            value: userSummary.active_count,
            color: chartPrimary,
          },
          {
            name: "Inactive",
            value: userSummary.inactive_count,
            color: chartPrimaryLight,
          },
        ]
      : [
          {
            name: "Founders",
            value: userSummary.role_breakdown.founder_only,
            color: chartPrimary,
          },
          {
            name: "Both founder & Investors",
            value: userSummary.role_breakdown.both_founder_and_investor,
            color: "#9ca3af",
          },
          {
            name: "Investors",
            value: userSummary.role_breakdown.investor_only,
            color: chartPrimaryLight,
          },
        ]
    : [];

  // Transform Engagement data for chart
  const engagementChartData = engagement
    ? engagement.weekly_data.map((item) => ({
        day: item.day,
        investors: item.investors_avg_time_minutes,
        founders: item.founders_avg_time_minutes,
      }))
    : [];

  // Transform Cost Token Usage data for chart
  const costTokenChartData = costTokenUsage
    ? costTokenUsage.monthly_data.map((item) => ({
        month: item.month,
        ...item.model_costs, // Spread model costs directly
      }))
    : [];

  // Format cost for display (handles 7 decimal places precision)
  const formatCost = (cost: number): string => {
    if (cost === 0) return "0.00";
    if (cost < 0.01) return cost.toFixed(7);
    if (cost < 1) return cost.toFixed(4);
    return cost.toFixed(2);
  };

  // Get dynamic font size class based on formatted cost length
  const getCostFontSize = (cost: number): string => {
    const formatted = formatCost(cost);
    const length = formatted.length;

    // For very long decimal values (like 0.0007228 = 9 chars), use smaller font
    if (length > 8) return "text-xl"; // Smaller for many decimals
    if (length > 6) return "text-2xl"; // Medium for moderate decimals
    return "text-3xl"; // Large for whole numbers or short decimals
  };

  // Color mapping for models
  const modelColors: Record<string, string> = {
    "google/gemini-2.5-flash": chartPrimary,
    "gpt-4": chartPrimaryLight,
    "claude-3-opus": chartPrimaryMuted,
    "ragflow-default": "#10b981",
  };

  const getModelColor = (model: string): string => {
    return modelColors[model] || `#${Math.floor(Math.random() * 16777215).toString(16)}`;
  };

  // Transform Company Summary data for chart
  const companySummaryChartData = companySummary
    ? companySummary.monthly_data.map((item) => ({
        month: item.month,
        "Founder-only": item.founder_count,
        "Investor-only": item.investor_count,
        "Both roles": item.both_count,
      }))
    : [];

  // Format Y-axis values (for company summary - whole numbers only, no suffix)
  const formatYAxis = (value: number) => {
    return Math.round(value).toString();
  };

  // Format Y-axis values with "$" prefix (for cost token usage)
  const formatCostYAxis = (value: number) => {
    return `$${value}`;
  };

  // Circular progress component
  const CircularProgress = ({ value, max = 100, size = 60 }: { value: number | null; max?: number; size?: number }) => {
    if (value === null) {
      return (
        <div
          className="flex items-center justify-center"
          style={{ width: size, height: size }}
        >
          <span className="text-xs font-semibold text-muted-foreground">N/A</span>
        </div>
      );
    }

    const percentage = (value / max) * 100;
    const radius = (size - 8) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <div
        className="relative inline-flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <svg
          width={size}
          height={size}
          className="-rotate-90 transform"
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#e5e7eb"
            strokeWidth="6"
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#10b981"
            strokeWidth="6"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-300"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-semibold text-foreground">
            {value}/{max}
          </span>
        </div>
      </div>
    );
  };

  // Mini trend line chart component
  const TrendChart = ({ score, trend }: { score: number | null; trend: "up" | "down" | "neutral" }) => {
    if (score === null) {
      return <span className="text-sm font-semibold text-muted-foreground">N/A</span>;
    }

    const width = 60;
    const height = 30;
    const padding = 4;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;

    // Create simple trend data based on trend direction
    const data =
      trend === "up"
        ? [score - 5, score - 3, score - 1, score]
        : trend === "down"
          ? [score + 5, score + 3, score + 1, score]
          : [score, score, score, score];

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;

    const points = data
      .map((value, index) => {
        const x = padding + (index / (data.length - 1 || 1)) * chartWidth;
        const y = padding + chartHeight - ((value - min) / range) * chartHeight;
        return `${x},${y}`;
      })
      .join(" ");

    const strokeColor = trend === "up" ? "#10b981" : trend === "down" ? "#ef4444" : "#6b7280";

    return (
      <div className="flex items-center gap-2">
        <svg
          width={width}
          height={height}
          className="overflow-visible"
        >
          <polyline
            points={points}
            fill="none"
            stroke={strokeColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="text-sm font-semibold text-foreground">{score}/100</span>
      </div>
    );
  };

  // Handle column sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleDownloadReport = async () => {
    if (duration === "custom") {
      if (!customDateFrom || !customDateTo) {
        toast({
          title: "Select a date range",
          description: "Please pick both start and end dates before downloading the report.",
          variant: "destructive",
        });
        return;
      }

      if (customDateFrom > customDateTo) {
        toast({
          title: "Invalid date range",
          description: "The start date must be earlier than the end date.",
          variant: "destructive",
        });
        return;
      }
    }

    setIsReportDownloading(true);
    try {
      const params: Parameters<typeof adminService.downloadAnalyticsReport>[0] = {
        duration,
        sort_by: sortBy,
        leaderboard_limit: LEADERBOARD_LIMIT,
      };

      if (duration === "custom" && customDateFrom && customDateTo) {
        params.date_from = customDateFrom.toISOString();
        params.date_to = customDateTo.toISOString();
      }

      if (costModel) {
        params.model_name = costModel;
      }

      const { data, error } = await adminService.downloadAnalyticsReport(params);
      if (error || !data) {
        const message = error ? await extractErrorMessage(error) : "Unable to download the analytics report.";
        toast({
          title: "Unable to download report",
          description: message || "Unable to download the analytics report.",
          variant: "destructive",
        });
        return;
      }

      const downloadUrl = URL.createObjectURL(data.blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = data.filename ?? "admin_analytics_report.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      const message = await extractErrorMessage(err);
      toast({
        title: "Unable to download report",
        description: message || "Unable to download the analytics report.",
        variant: "destructive",
      });
    } finally {
      setIsReportDownloading(false);
    }
  };

  // Sort leaderboard data
  const sortedLeaderboard = [...leaderboard].sort((a, b) => {
    if (!sortColumn) return 0;

    let aValue: number | string | null;
    let bValue: number | string | null;

    switch (sortColumn) {
      case "rank":
        aValue = a.rank;
        bValue = b.rank;
        break;
      case "name":
        aValue = a.company_name;
        bValue = b.company_name;
        break;
      case "onboardingScore":
        aValue = a.onboarding_score;
        bValue = b.onboarding_score;
        break;
      case "currentScore":
        aValue = a.current_score;
        bValue = b.current_score;
        break;
      default:
        return 0;
    }

    // Handle null values
    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return 1;
    if (bValue === null) return -1;

    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortDirection === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    return sortDirection === "asc" ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
  });

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Analytics</h1>
          </div>
          <div className="flex flex-col items-stretch gap-2 sm:items-end">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground sm:justify-end">
              <Calendar className="h-4 w-4 text-primary" />
              <span>Filter Period</span>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div
                className="relative w-full sm:w-auto"
                ref={dropdownRef}
              >
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex w-full items-center justify-between gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm text-muted-foreground shadow-sm hover:bg-muted hover:text-foreground"
                >
                  <span>{formatDateRangeDisplay()}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute right-0 top-full z-50 mt-2 w-56 rounded-lg border border-border bg-card shadow-lg">
                    <div className="py-1">
                      {(["24h", "7d", "2w", "1m", "4m", "custom"] as const).map((option) => (
                        <button
                          key={option}
                          onClick={() => {
                            if (option === "custom") {
                              // For custom, set duration but keep dropdown open to show date pickers
                              setDuration("custom");
                              if (!customDateFrom || !customDateTo) {
                                // Set default custom dates if not set
                                const to = new Date();
                                const from = new Date();
                                from.setDate(from.getDate() - 30);
                                setCustomDateFrom(from);
                                setCustomDateTo(to);
                              }
                              // Don't close dropdown - let user pick dates
                            } else {
                              setDuration(option);
                              setIsDropdownOpen(false);
                            }
                          }}
                          className={`w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted ${
                            duration === option ? "bg-primary/10 text-primary" : ""
                          }`}
                        >
                          {getDurationLabel(option)}
                        </button>
                      ))}
                    </div>
                    {duration === "custom" && (
                      <div className="border-t border-border p-4">
                        <div className="space-y-3">
                          <div>
                            <label className="mb-1 block text-xs font-medium text-foreground">From</label>
                            <input
                              type="date"
                              value={customDateFrom ? customDateFrom.toISOString().split("T")[0] : ""}
                              onChange={(e) => {
                                if (e.target.value) {
                                  setCustomDateFrom(new Date(e.target.value));
                                }
                              }}
                              className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-foreground">To</label>
                            <input
                              type="date"
                              value={customDateTo ? customDateTo.toISOString().split("T")[0] : ""}
                              onChange={(e) => {
                                if (e.target.value) {
                                  setCustomDateTo(new Date(e.target.value));
                                }
                              }}
                              className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          </div>
                          <button
                            onClick={() => {
                              if (customDateFrom && customDateTo) {
                                setIsDropdownOpen(false);
                              }
                            }}
                            disabled={!customDateFrom || !customDateTo}
                            className="w-full rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-muted"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  void handleDownloadReport();
                }}
                disabled={isReportDownloading}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground shadow-sm transition hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isReportDownloading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span>Preparing...</span>
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 text-muted-foreground" />
                    <span>Download PDF</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="p-6">
              {keyMetricsLoading ? (
                <div className="flex items-center gap-4">
                  <Skeleton className="h-16 w-16 flex-shrink-0 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-8 w-20" />
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-12" />
                  </div>
                </div>
              ) : keyMetricsError ? (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <span>{keyMetricsError}</span>
                  <Button variant="ghost" size="sm" onClick={() => void fetchKeyMetrics()} className="h-6 px-1 text-xs">
                    <RotateCw className="h-3 w-3" />
                  </Button>
                </div>
              ) : keyMetrics ? (
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary">
                      <Users className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-3xl font-bold text-foreground">{keyMetrics.active_users.value}</div>
                    <div className="mt-1 text-sm text-muted-foreground">Active Users</div>
                    <div
                      className={`mt-2 flex items-center gap-1 text-xs font-medium ${
                        keyMetrics.active_users.trend_direction === "up"
                          ? "text-green-600"
                          : keyMetrics.active_users.trend_direction === "down"
                            ? "text-destructive"
                            : "text-muted-foreground"
                      }`}
                    >
                      {keyMetrics.active_users.trend_direction === "up" ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : keyMetrics.active_users.trend_direction === "down" ? (
                        <TrendingDown className="h-3 w-3" />
                      ) : null}
                      {keyMetrics.active_users.trend_direction === "up"
                        ? "+"
                        : keyMetrics.active_users.trend_direction === "down"
                          ? "-"
                          : ""}
                      {Math.abs(keyMetrics.active_users.trend_percentage)}%
                    </div>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              {keyMetricsLoading ? (
                <div className="flex items-center gap-4">
                  <Skeleton className="h-16 w-16 flex-shrink-0 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-8 w-20" />
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-12" />
                  </div>
                </div>
              ) : keyMetricsError ? (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <span>{keyMetricsError}</span>
                  <Button variant="ghost" size="sm" onClick={() => void fetchKeyMetrics()} className="h-6 px-1 text-xs">
                    <RotateCw className="h-3 w-3" />
                  </Button>
                </div>
              ) : keyMetrics ? (
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary">
                      <Users className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-3xl font-bold text-foreground">{keyMetrics.investor_intros.value}</div>
                    <div className="mt-1 text-sm text-muted-foreground">Investor Intros made</div>
                    <div
                      className={`mt-2 flex items-center gap-1 text-xs font-medium ${
                        keyMetrics.investor_intros.trend_direction === "up"
                          ? "text-green-600"
                          : keyMetrics.investor_intros.trend_direction === "down"
                            ? "text-destructive"
                            : "text-muted-foreground"
                      }`}
                    >
                      {keyMetrics.investor_intros.trend_direction === "up" ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : keyMetrics.investor_intros.trend_direction === "down" ? (
                        <TrendingDown className="h-3 w-3" />
                      ) : null}
                      {keyMetrics.investor_intros.trend_direction === "up"
                        ? "+"
                        : keyMetrics.investor_intros.trend_direction === "down"
                          ? "-"
                          : ""}
                      {Math.abs(keyMetrics.investor_intros.trend_percentage)}%
                    </div>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              {keyMetricsLoading ? (
                <div className="flex items-center gap-4">
                  <Skeleton className="h-16 w-16 flex-shrink-0 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-8 w-20" />
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-12" />
                  </div>
                </div>
              ) : keyMetricsError ? (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <span>{keyMetricsError}</span>
                  <Button variant="ghost" size="sm" onClick={() => void fetchKeyMetrics()} className="h-6 px-1 text-xs">
                    <RotateCw className="h-3 w-3" />
                  </Button>
                </div>
              ) : keyMetrics ? (
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary">
                      <FileText className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className={`${getCostFontSize(keyMetrics.total_ai_cost.value)} font-bold text-foreground`}>
                      ${formatCost(keyMetrics.total_ai_cost.value)}
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">Total AI cost</div>
                    <div
                      className={`mt-2 flex items-center gap-1 text-xs font-medium ${
                        keyMetrics.total_ai_cost.trend_direction === "up"
                          ? "text-green-600"
                          : keyMetrics.total_ai_cost.trend_direction === "down"
                            ? "text-destructive"
                            : "text-muted-foreground"
                      }`}
                    >
                      {keyMetrics.total_ai_cost.trend_direction === "up" ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : keyMetrics.total_ai_cost.trend_direction === "down" ? (
                        <TrendingDown className="h-3 w-3" />
                      ) : null}
                      {keyMetrics.total_ai_cost.trend_direction === "up"
                        ? "+"
                        : keyMetrics.total_ai_cost.trend_direction === "down"
                          ? "-"
                          : ""}
                      {Math.abs(keyMetrics.total_ai_cost.trend_percentage)}%
                    </div>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              {keyMetricsLoading ? (
                <div className="flex items-center gap-4">
                  <Skeleton className="h-16 w-16 flex-shrink-0 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-8 w-20" />
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-12" />
                  </div>
                </div>
              ) : keyMetricsError ? (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <span>{keyMetricsError}</span>
                  <Button variant="ghost" size="sm" onClick={() => void fetchKeyMetrics()} className="h-6 px-1 text-xs">
                    <RotateCw className="h-3 w-3" />
                  </Button>
                </div>
              ) : keyMetrics ? (
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary">
                      <Users className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-3xl font-bold text-foreground">{keyMetrics.dealstack_references.value}</div>
                    <div className="mt-1 text-sm text-muted-foreground">Dealstack Connect</div>
                    <div
                      className={`mt-2 flex items-center gap-1 text-xs font-medium ${
                        keyMetrics.dealstack_references.trend_direction === "up"
                          ? "text-green-600"
                          : keyMetrics.dealstack_references.trend_direction === "down"
                            ? "text-destructive"
                            : "text-muted-foreground"
                      }`}
                    >
                      {keyMetrics.dealstack_references.trend_direction === "up" ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : keyMetrics.dealstack_references.trend_direction === "down" ? (
                        <TrendingDown className="h-3 w-3" />
                      ) : null}
                      {keyMetrics.dealstack_references.trend_direction === "up"
                        ? "+"
                        : keyMetrics.dealstack_references.trend_direction === "down"
                          ? "-"
                          : ""}
                      {Math.abs(keyMetrics.dealstack_references.trend_percentage)}%
                    </div>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* User Summary - Donut Chart */}
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle>User Summary</CardTitle>
                <div className="relative w-full sm:w-auto">
                  <select
                    value={userSummaryFilter}
                    onChange={(e) => setUserSummaryFilter(e.target.value as "role" | "status")}
                    className="w-full appearance-none rounded-md border border-border bg-muted px-3 py-2 pr-8 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary sm:w-auto"
                  >
                    <option value="role">Founders vs Investors</option>
                    <option value="status">Active vs Inactive</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* New Users Card */}
              {userSummaryLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-16 w-full rounded-lg" />
                  <div className="flex flex-col items-center gap-6 md:flex-row md:items-start">
                    <Skeleton className="h-[240px] w-[240px] flex-shrink-0 rounded-full" />
                    <div className="w-full flex-1 space-y-2">
                      <Skeleton className="h-16 w-full rounded-lg" />
                      <Skeleton className="h-16 w-full rounded-lg" />
                      <Skeleton className="h-16 w-full rounded-lg" />
                    </div>
                  </div>
                </div>
              ) : userSummaryError ? (
                <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-destructive">
                  <p>{userSummaryError}</p>
                  <Button variant="ghost" size="sm" onClick={() => void fetchUserSummary()} className="flex items-center gap-1 text-xs">
                    <RotateCw className="h-3 w-3" /> Try again
                  </Button>
                </div>
              ) : userSummary ? (
                <>
                  <div className="flex flex-col gap-3 rounded-lg bg-primary/10 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary">
                        <span className="text-lg font-bold text-primary-foreground">{userSummary.new_users_count}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">New users</span>
                        <div className="h-2 w-2 rounded-full bg-primary"></div>
                      </div>
                    </div>
                    <Link
                      to="/app/admin/users"
                      className="flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 sm:shrink-0"
                    >
                      Manage users
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>

                  {/* Main Content Area */}
                  {userSummaryChartData.length > 0 ? (
                    <div className="flex flex-col items-center gap-6 md:flex-row md:items-start">
                      {/* Donut Chart */}
                      <div className="h-[240px] w-full max-w-[240px] flex-shrink-0 md:w-[240px]">
                        <ResponsiveContainer
                          width="100%"
                          height="100%"
                        >
                          <PieChart>
                            <Pie
                              data={userSummaryChartData}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={90}
                              paddingAngle={2}
                              dataKey="value"
                              startAngle={90}
                              endAngle={-270}
                            >
                              {userSummaryChartData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={entry.color}
                                />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Stacked Cards */}
                      <div className="w-full flex-1 space-y-2 md:w-auto">
                        {[...userSummaryChartData]
                          .sort((a, b) => {
                            if (userSummaryFilter === "status") {
                              // Order: Active, Inactive
                              const order = ["Active", "Inactive"];
                              return order.indexOf(a.name) - order.indexOf(b.name);
                            } else {
                              // Order: Both founder & Investors, Founders, Investors
                              const order = ["Both founder & Investors", "Founders", "Investors"];
                              return order.indexOf(a.name) - order.indexOf(b.name);
                            }
                          })
                          .map((item, index) => (
                            <Card
                              key={index}
                              className="p-3"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-2xl font-bold text-foreground">{item.value}</div>
                                  <div className="mt-0.5 text-xs text-muted-foreground">{item.name}</div>
                                </div>
                                <div
                                  className="h-3 w-3 shrink-0 rounded"
                                  style={{ backgroundColor: item.color }}
                                />
                              </div>
                            </Card>
                          ))}
                      </div>
                    </div>
                  ) : (
                    <EmptyState title="No data available" description="No data was recorded for the selected period." className="border-none bg-transparent" />
                  )}
                </>
              ) : null}
            </CardContent>
          </Card>

          {/* Engagement - Area Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Engagement</CardTitle>
              <CardDescription>User engagement over time</CardDescription>
            </CardHeader>
            <CardContent>
              {engagementLoading ? (
                <div className="space-y-3">
                  <div className="flex gap-6">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                  <Skeleton className="h-[300px] w-full rounded-lg" />
                </div>
              ) : engagementError ? (
                <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-destructive">
                  <p>{engagementError}</p>
                  <Button variant="ghost" size="sm" onClick={() => void fetchEngagement()} className="flex items-center gap-1 text-xs">
                    <RotateCw className="h-3 w-3" /> Try again
                  </Button>
                </div>
              ) : engagement ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-primary/40" />
                      <span className="text-sm text-muted-foreground">Investors</span>
                      <span className="text-sm font-semibold text-foreground">
                        Avg. Time {formatTime(engagement.investors_avg_time_minutes)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-primary" />
                      <span className="text-sm text-muted-foreground">Founders</span>
                      <span className="text-sm font-semibold text-foreground">
                        Avg. Time {formatTime(engagement.founders_avg_time_minutes)}
                      </span>
                    </div>
                  </div>
                  <ResponsiveContainer
                    width="100%"
                    height={300}
                  >
                    <AreaChart data={engagementChartData}>
                      <defs>
                        <linearGradient
                          id="colorInvestors"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={chartPrimaryLight}
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="95%"
                            stopColor={chartPrimaryLight}
                            stopOpacity={0}
                          />
                        </linearGradient>
                        <linearGradient
                          id="colorFounders"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={chartPrimary}
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="95%"
                            stopColor={chartPrimary}
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="investors"
                        stroke={chartPrimaryLight}
                        fillOpacity={1}
                        fill="url(#colorInvestors)"
                      />
                      <Area
                        type="monotone"
                        dataKey="founders"
                        stroke={chartPrimary}
                        fillOpacity={1}
                        fill="url(#colorFounders)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <EmptyState title="No data available" description="No data was recorded for the selected period." className="border-none bg-transparent" />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Stacked Bar Charts Section */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Cost & Token Usage - Stacked Bar Chart */}
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle>Cost & Token Usage</CardTitle>
                </div>
                <div className="relative w-full sm:w-auto">
                  <select
                    value={costModel}
                    onChange={(e) => setCostModel(e.target.value)}
                    disabled={costTokenLoading}
                    className="appearance-none rounded-md border border-border bg-card px-3 py-1.5 pr-8 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                  >
                    <option value="">All Models</option>
                    {costTokenUsage?.available_models.map((model) => (
                      <option
                        key={model}
                        value={model}
                      >
                        {model}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {costTokenLoading ? (
                <div className="space-y-3">
                  <div className="flex gap-4">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-[300px] w-full rounded-lg" />
                </div>
              ) : costTokenError ? (
                <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-destructive">
                  <p>{costTokenError}</p>
                  <Button variant="ghost" size="sm" onClick={() => void fetchCostTokenUsage()} className="flex items-center gap-1 text-xs">
                    <RotateCw className="h-3 w-3" /> Try again
                  </Button>
                </div>
              ) : costTokenChartData.length > 0 ? (
                <div className="space-y-4">
                  {/* Legend */}
                  {costTokenUsage?.available_models && costTokenUsage.available_models.length > 0 && (
                    <div className="flex flex-wrap items-center gap-4">
                      {costTokenUsage.available_models.map((model) => (
                        <div
                          key={model}
                          className="flex items-center gap-2"
                        >
                          <div
                            className="h-3 w-3 rounded"
                            style={{ backgroundColor: getModelColor(model) }}
                          />
                          <span className="text-sm text-foreground">{model}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Chart */}
                  <ResponsiveContainer
                    width="100%"
                    height={300}
                  >
                    <BarChart
                      data={costTokenChartData}
                      margin={{ left: 20, right: 20 }}
                    >
                      <XAxis dataKey="month" />
                      <YAxis
                        tickFormatter={formatCostYAxis}
                        width={60}
                      />
                      <Tooltip />
                      {costTokenUsage?.available_models.map((model) => (
                        <Bar
                          key={model}
                          dataKey={model}
                          stackId="a"
                          fill={getModelColor(model)}
                          name={model}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <EmptyState title="No data available" description="No data was recorded for the selected period." className="border-none bg-transparent" />
              )}
            </CardContent>
          </Card>

          {/* Company Summary - Stacked Bar Chart */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Company Summary</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {companySummaryLoading ? (
                <div className="space-y-3">
                  <div className="flex gap-4">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-[300px] w-full rounded-lg" />
                </div>
              ) : companySummaryError ? (
                <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-destructive">
                  <p>{companySummaryError}</p>
                  <Button variant="ghost" size="sm" onClick={() => void fetchCompanySummary()} className="flex items-center gap-1 text-xs">
                    <RotateCw className="h-3 w-3" /> Try again
                  </Button>
                </div>
              ) : companySummaryChartData.length > 0 ? (
                <div className="space-y-4">
                  {/* Legend */}
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-3 w-3 rounded"
                        style={{ backgroundColor: stackColors.item1 }}
                      />
                      <span className="text-sm text-foreground">Founder-only</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-3 w-3 rounded"
                        style={{ backgroundColor: stackColors.item2 }}
                      />
                      <span className="text-sm text-foreground">Investor-only</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-3 w-3 rounded"
                        style={{ backgroundColor: stackColors.item3 }}
                      />
                      <span className="text-sm text-foreground">Both roles</span>
                    </div>
                  </div>
                  {/* Chart */}
                  <ResponsiveContainer
                    width="100%"
                    height={300}
                  >
                    <BarChart data={companySummaryChartData}>
                      <XAxis dataKey="month" />
                      <YAxis
                        tickFormatter={formatYAxis}
                        allowDecimals={false}
                      />
                      <Tooltip />
                      <Bar
                        dataKey="Founder-only"
                        stackId="a"
                        fill={stackColors.item1}
                      />
                      <Bar
                        dataKey="Investor-only"
                        stackId="a"
                        fill={stackColors.item2}
                      />
                      <Bar
                        dataKey="Both roles"
                        stackId="a"
                        fill={stackColors.item3}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <EmptyState title="No data available" description="No data was recorded for the selected period." className="border-none bg-transparent" />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Leaderboard Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Leaderboard</CardTitle>
                <CardDescription className="mt-1">Ranked list of companies with investment scores</CardDescription>
              </div>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "current_score" | "onboarding_score" | "name")}
                  disabled={leaderboardLoading}
                  className="appearance-none rounded-md border border-border bg-card px-3 py-1.5 pr-8 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                >
                  <option value="current_score">Current Score</option>
                  <option value="onboarding_score">Onboarding Score</option>
                  <option value="name">Name</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {leaderboardLoading ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <tbody>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <tr
                        key={i}
                        className="border-b"
                      >
                        <td className="px-4 py-4">
                          <Skeleton className="h-4 w-8" />
                        </td>
                        <td className="px-4 py-4">
                          <Skeleton className="h-4 w-36" />
                        </td>
                        <td className="px-4 py-4">
                          <Skeleton className="h-12 w-12 rounded-full" />
                        </td>
                        <td className="px-4 py-4">
                          <Skeleton className="h-4 w-24" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : leaderboardError ? (
              <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-destructive">
                <p>{leaderboardError}</p>
                <Button variant="ghost" size="sm" onClick={() => void fetchLeaderboard()} className="flex items-center gap-1 text-xs">
                  <RotateCw className="h-3 w-3" /> Try again
                </Button>
              </div>
            ) : sortedLeaderboard.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                        <button
                          onClick={() => handleSort("rank")}
                          className="flex items-center gap-1 hover:text-foreground"
                        >
                          No:
                          <ChevronsUpDown className="h-3 w-3" />
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                        <button
                          onClick={() => handleSort("name")}
                          className="flex items-center gap-1 hover:text-foreground"
                        >
                          Name:
                          <ChevronsUpDown className="h-3 w-3" />
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                        <button
                          onClick={() => handleSort("onboardingScore")}
                          className="flex items-center gap-1 hover:text-foreground"
                        >
                          <span className="hidden sm:inline">Investment score after On-boarding</span>
                          <span className="sm:hidden">Onboarding Score</span>
                          <ChevronsUpDown className="h-3 w-3" />
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                        <button
                          onClick={() => handleSort("currentScore")}
                          className="flex items-center gap-1 hover:text-foreground"
                        >
                          <span className="hidden sm:inline">Current Investment score</span>
                          <span className="sm:hidden">Current Score</span>
                          <ChevronsUpDown className="h-3 w-3" />
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLeaderboard.map((item) => (
                      <tr
                        key={item.company_id}
                        className="border-b hover:bg-muted"
                      >
                        <td className="px-4 py-4 text-sm text-foreground">#{item.rank}</td>
                        <td className="px-4 py-4">
                          <Link
                            to="/app/companies/$company_id"
                            params={{ company_id: item.company_id }}
                            className="text-sm font-medium text-primary hover:text-primary/80 hover:underline"
                          >
                            {item.company_name}
                          </Link>
                        </td>
                        <td className="px-4 py-4">
                          <CircularProgress value={item.onboarding_score} />
                        </td>
                        <td className="px-4 py-4">
                          <TrendChart
                            score={item.current_score}
                            trend={item.trend}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState title="No data available" description="No companies recorded for the selected period." className="border-none bg-transparent" />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
